import { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../UIToolkit/Inputs/SystemValidationTextField";
import { SettingRow } from "../../UIToolkit/molecules/SettingRow";
import { NUMERIC_INPUT_WIDTH, SCHEDULE_EXPORT_TEST_ID } from "./consts";

export type DayColumnWidthFieldProps = {
	/** Applies one pixel width to every day column. */
	onApply: (widthPx: number) => void;
	disabled?: boolean;
};

/** Matches the settings save, so a typed width and a toggled checkbox land in one PUT. */
const DEBOUNCE_MS = 600;

/** Below this a day column cannot show even one digit; above it, one column fills the page. */
const MIN_WIDTH_PX = 8;
const MAX_WIDTH_PX = 400;

/**
 * Keep only digits.
 *
 * The field is `type="text"` rather than `type="number"` on purpose: a number input reports
 * `value === ""` for a partially-typed `-`, `.` or `e` while still SHOWING it, so the visible text and
 * the state diverge and stray characters appear to be accepted. Owning the raw string and filtering it
 * is the only way to guarantee digits only. `inputMode="numeric"` keeps the mobile keypad.
 *
 * Digits-only also removes whole classes of bad input by construction: no sign, no decimal point, no
 * exponent — so the only thing left to validate is the ceiling.
 */
const toDigits = (raw: string): string => raw.replace(/\D/g, "");

/** Whether a typed value is complete enough to APPLY. Drives the debounce, not the red border. */
const isApplicable = (digits: string): boolean => {
	if (digits === "") return false;
	const parsed = Number(digits);
	return parsed >= MIN_WIDTH_PX && parsed <= MAX_WIDTH_PX;
};

/**
 * Whether to mark the field. A SEPARATE question from whether to apply the value.
 *
 * Empty is the resting state — the box empties itself once applied, and the field validates every
 * value after the first keystroke, so treating empty as invalid would paint every SUCCESS red.
 *
 * A value under the minimum is treated as still-being-typed rather than wrong: "4" on the way to "40"
 * should not flash. Only the ceiling, which no further digit can rescue, is marked.
 */
const isRejected = (digits: string): boolean => digits !== "" && Number(digits) > MAX_WIDTH_PX;

/**
 * "Set every day column to this width" — a COMMAND, not a bound field.
 *
 * It applies a width and empties itself, so there is no stored value it could display: a per-column
 * drag afterwards changes one column and nothing else.
 */
export const DayColumnWidthField: FC<DayColumnWidthFieldProps> = ({ onApply, disabled }) => {
	const { t } = useTranslation();
	const [typed, setTyped] = useState("");

	// Read through a ref so the timer below depends only on the typed text: the owner rebuilds this
	// callback whenever any setting changes, and a new identity mid-type would restart the debounce.
	const apply = useRef(onApply);
	apply.current = onApply;

	// What the debounce below has not applied yet, so unmounting can finish the job.
	const pending = useRef<number | null>(null);

	// Applies the width once typing settles, then empties the box. The cleanup is the debounce: each
	// keystroke cancels the pending apply, so "1", "12", "120" resolves to one call with 120.
	useEffect(() => {
		if (!isApplicable(typed)) {
			pending.current = null;
			return;
		}
		pending.current = Number(typed);
		const timer = setTimeout(() => {
			apply.current(Number(typed));
			pending.current = null;
			setTyped("");
		}, DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [typed]);

	// Unmount-only, so a finished width is not thrown away by closing the popup or switching tabs inside
	// the debounce window. Both really unmount this component — `ScheduleExportBody` renders one tab or
	// the other — and the cleanup above only cancels.
	useEffect(
		() => () => {
			if (pending.current !== null) apply.current(pending.current);
		},
		[],
	);

	return (
		<SettingRow
			label={t("download.day.column.width")}
			control={
				<SystemValidationTextField
					value={typed}
					disabled={disabled}
					onChange={(event) => setTyped(toDigits(event.target.value))}
					inputType="text"
					injectedProps={{ slotProps: { htmlInput: { inputMode: "numeric" } } }}
					placeholder={t("download.day.column.width.placeholder")}
					reserveHeightForHelperText={false}
					formControlSx={{ width: NUMERIC_INPUT_WIDTH }}
					dataTestId={`${SCHEDULE_EXPORT_TEST_ID}-day-column-width`}
					validation={{
						validationCallback: (raw) => !isRejected(raw),
						performValidation: "onlyAfterInitialChange",
						showValidWhenValid: "never",
					}}
				/>
			}
		/>
	);
};
